import { Context, useContext } from 'react';

import { MODALS_DEFAULT_STATE } from '@/constant/modals';
import { SERVER_DATA_LAYOUT_TYPE_FALLBACK_VALUE } from '@/constant/serverData';
import { AppNavigationContext } from '@/context/AppNavigation';
import { DocsDataContext } from '@/context/DocsData';
import { FeedbackContext } from '@/context/Feedback';
import { HeaderRightSectionContext } from '@/context/HeaderRightSection';
import { ModalsContext } from '@/context/Modals';
import { PagesDataContext } from '@/context/PageData';
import { PageNavigationContext } from '@/context/PageNavigation';
import { ServerDataContext } from '@/context/ServerData';
import { AbsolutePath } from '@/types/navigation';
import { identity, isNil } from '@/utils/common';

const useStrictContext = <T>(context: Context<T | null>, fallbackValue: T, skipNotify = false) => {
  const value = useContext(context);
  if (isNil(value)) {
    if (!skipNotify) {
      console.error(`ctx not passed, fallback value ${JSON.stringify(fallbackValue)}`);
      return fallbackValue;
    }
    return fallbackValue;
  }
  return value;
};

export const usePageDataCtx = () =>
  useStrictContext(
    PagesDataContext,
    { metaPlatform: '', metaTagsStr: '', bodyString: '', isCrossPlatform: false, authRequired: false },
    true,
  );

export const usePageNavigationCtx = () =>
  useStrictContext(
    PageNavigationContext,
    {
      rootFolderSlug: '/' as AbsolutePath,
      sidebarHtmlString: '',
      TOCHtmlString: '',
      sidebarTitleData: { text: '' },
    },
    true,
  );

export const useHeaderRightSectionCtx = () =>
  useStrictContext(HeaderRightSectionContext, {
    isOpenBurgerContent: false,
    setIsOpenBurgerContent: identity,
    toggleIsOpenBurgerContent: identity<void>,
  });

export const useModalsCtx = () =>
  useStrictContext(ModalsContext, {
    openModal: identity,
    closeModal: identity,
    modalsState: MODALS_DEFAULT_STATE,
  });

export const useSerpFiltersCtx = () =>
  useStrictContext(DocsDataContext, {
    isLoadingDocsData: false,
    docsData: null,
  });

export const useServerDataCtx = () =>
  useStrictContext(ServerDataContext, {
    headers: { xLayoutType: SERVER_DATA_LAYOUT_TYPE_FALLBACK_VALUE },
  });

export const useFeedbackPropsCtx = () =>
  useStrictContext(FeedbackContext, {
    isLoadingSendFeedback: false,
    onChooseScore: identity,
    onSubmitForm: identity,
    isOpenThanksBlock: false,
    setIsOpenThanksBlock: identity,
    isOpenFeedbackForm: false,
    setIsOpenFeedbackForm: identity,
  });

export const useAppNavigationCtx = () =>
  useStrictContext(AppNavigationContext, { appNavigationConfig: [], activeAppNavigationItemByRoute: null });
